import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { PrioCriteriaPicker, computePrioWith, usePrioConfig, PRIO_LABEL } from './PrioCriteriaPicker';

// Final approval (Max, 22.10.2026): the one final approver confirms the deal and may re-tick the prio criteria first.
export function FinalApprovalDialog({ project, onClose, onDone }) {
  const cfg = usePrioConfig();
  const [ids, setIds] = useState(project.prio_criteria || []);
  const [busy, setBusy] = useState(false);
  const picked = ids.length ? computePrioWith(ids, cfg?.rules, cfg?.criteria) : null;
  const isPass = picked === 'PASS';
  const changed = picked && (picked !== project.prio || JSON.stringify([...ids].sort()) !== JSON.stringify([...(project.prio_criteria || [])].sort()));
  const proposer = (project.approval_proposed?.proposed_by || '').split(' ')[0];

  const approve = async () => {
    setBusy(true);
    try {
      const r = await api.confirmApproval(project.id, ids.length ? { prio_criteria: ids } : {});
      toast.success(r.data.message);
      onClose(); onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || 'Freigabe fehlgeschlagen'); }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg max-h-[90vh] overflow-y-auto" data-testid="final-approval-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-400" /> Finale Freigabe · {project.random_project_name || project.name}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {proposer ? `${proposer} hat den Deal${project.prio ? ` als Prio ${project.prio}` : ''} vorgeschlagen. ` : ''}
            Prio-Kriterien prüfen und bei Bedarf korrigieren — mit der Freigabe geht der Deal nach OLTA Approved, die Kontakt-Aufgabe startet.
          </DialogDescription>
        </DialogHeader>
        {project.approval_proposed?.note && (
          <p className="text-xs text-sky-300/90" data-testid="final-approval-note">
            <span className="font-medium">{proposer}'s Note:</span> „{project.approval_proposed.note}“
          </p>
        )}
        <PrioCriteriaPicker value={ids} onChange={setIds} />
        {isPass && (
          <p className="text-[11px] text-amber-300" data-testid="final-approval-pass-hint">
            Diese Kriterien ergeben keine Prio, sondern einen Pass (Bench) — Vorschlag ablehnen und den Deal passen.
          </p>
        )}
        {changed && !isPass && (
          <p className="text-[11px] text-amber-300" data-testid="final-approval-prio-changed">
            Prio wird mit der Freigabe auf {PRIO_LABEL(picked)} gesetzt{project.prio ? ` (vorher Prio ${project.prio})` : ''}.
          </p>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="final-approval-cancel-btn">Abbrechen</Button>
          <Button onClick={approve} disabled={busy || isPass} data-testid="final-approval-confirm-btn"
            className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
            Freigeben{picked && !isPass ? ` als ${PRIO_LABEL(picked)}` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
